/* eslint-disable no-unreachable */
import { asyncThunkCreator, buildCreateSlice } from "@reduxjs/toolkit";
import { isEqual, isObject } from "lodash";

export const hasAccess = ({ type, resource }) => {
  try {
    const session = localStorage.getItem("activeSession");
    if (!session) return false;

    const { RESOURCES } = JSON.parse(session);
    if (!RESOURCES || !Array.isArray(RESOURCES)) return false;

    const param = type === "path" ? "URL" : "RESOURCE_NAME";
    let _hasAccess = RESOURCES.some(
      (res) =>
        res?.[param]?.toLowerCase().trim() === resource?.toLowerCase().trim(),
    );
    return _hasAccess;
  } catch {
    return false;
  }
};

export const getAPIHeaders = ({ queryParams = null, headers = {} }) => {
  return {
    headers: {
      Accept: "application/json",
      language: localStorage.getItem("currentLanguage"),
      languageVersion: localStorage.getItem("languageVersion"),
      ...headers,
    },
    params: queryParams,
    withCredentials: true,
    validateStatus: (status) => {
      return status < 500;
    },
  };
};

export const createSliceWithThunks = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
});

export const dateFormat = "DD-MMM-YYYY";
export const dateFormatWithTime = "DD-MMM-YYYY HH:mm:ss";
export const oracleDateFormat = "DD-MMM-YYYY hh:mm:ss A";

export const unformatNumber = (number) => {
  return number && number.replace(/,/g, "");
};

export const formatNumber = (_number) => {
  if (_number !== null && _number !== undefined) {
    let _formattedNumber = "";
    if (_number !== "") {
      //control Zero (0)
      if (_number?.toString() === "0") {
        return _number;
      } else {
        let number = _number.toString();
        let last = number.charAt(number.length - 1);

        if (last === ".") {
          if (number.substr(0, number.length - 1).includes(".")) {
            _formattedNumber = number.substr(0, number.length - 1);
          } else {
            _formattedNumber = _number;
          }
        } else {
          let cleaned = number.replace(/,/g, "");
          _formattedNumber = new Intl.NumberFormat().format(
            parseFloat(cleaned),
          );
        }
      }
      // _formattedNumber = new Intl.NumberFormat("en-EN").format(number);
    }
    return _formattedNumber;
  }
};

export const base64FileDetails = (base64String) => {
  let _strings = base64String.split(",");
  let file = {};
  switch (
    _strings[0] //check files's extension
  ) {
    case "data:application/pdf;base64":
      file = {
        ...file,
        type: "Document",
        extension: "pdf",
      };
      break;
    case "data:image/jpeg;base64":
      file = {
        ...file,
        type: "Image",
        extension: "jpeg",
      };
      break;
    case "data:image/png;base64":
      file = {
        ...file,
        type: "Image",
        extension: "png",
      };
      break;
    default:
      //should write cases for more images types
      file = {
        ...file,
        type: "Image",
        extension: "jpg",
      };
      break;
  }

  return file;
};

export const generateRandomNumber = (length = 1) => {
  let result = "";
  let characters = "0123456789";
  let charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const generateRandomSpecialCharacter = async (length = 1) => {
  let result = "";
  let characters = "_.@$&*";
  let charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const getBase64 = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const capitalizeWord = (word = "") => {
  if (word?.trim().length > 1) {
    let _rest = word.slice(1);
    return `${word.charAt(0).toUpperCase()}${_rest?.toLocaleLowerCase()}`;
  } else {
    return word?.toUpperCase();
  }
};

export const isEmpty = (val) => {
  return val === null || val?.toString()?.trim() === "" || val === undefined;
};

export const getObjectDifference = (origObj, newObj) => {
  const changes = (newObj, origObj) => {
    let arrayIndexCounter = 0;

    return Object.keys(newObj).reduce((result, key) => {
      const newValue = newObj[key];
      const origValue = origObj[key];

      if (!isEqual(newValue, origValue)) {
        let resultKey = Array.isArray(origObj) ? arrayIndexCounter++ : key;
        result[resultKey] =
          isObject(newValue) && isObject(origValue)
            ? changes(newValue, origValue)
            : newValue;
      }

      return result;
    }, {});
  };

  return changes(newObj, origObj);
};

export const uploadTypes = [
  // { label: "Audio Book", value: "audioBook" },
  // { label: "Events", value: "event" },
  { label: "Circulars", value: "circular" },
  // { label: "Dissertation", value: "dissertation" },
  { label: "Books", value: "eBook" },
  { label: "Period Reports", value: "journal" },
  // { label: "Manuscript", value: "manuscript" },
  { label: "Research Papers", value: "researchPaper" },
  { label: "Speeches", value: "speech" },
];

export const journalCategories = [
  "Monthly Economic Report",
  "Quarterly Statistical Bulletin",
  "Monetary Policy Report",
  "Monetary Policy Statement",
  "Financial Stability Report",
  "Annual report",
  "Other",
];

export const translate = (resource) => {
  let currentLanguage = localStorage.getItem("currentLanguage");
  let languageResources = JSON.parse(localStorage.getItem("languageResources"));
  return languageResources?.[resource]?.[currentLanguage] || resource;
};

export const roles = [
  {
    roleId: 1,
    roleName: "ADMINISTRATOR",
  },
  // {
  //   roleId: 2,
  //   roleName: "STUDENT",
  // },
  {
    roleId: 3,
    roleName: "LIBRARIAN",
  },
  {
    roleId: 4,
    roleName: "AUDITOR",
  },
];

export const translateStatus = (status) => {
  switch (status) {
    case "A":
      return "Active";
    case "I":
      return "Inactive";
    case "D":
      return "Disabled";
    default:
      return "N/A";
  }
};
